// src/services/logout.js
//
// One logout, used by every "Log Out" button.
//
// There were SEVEN separate logout handlers (Dashboard, CoordinatorPage,
// ReceptionPage, ReferralPage, MonitorsPage, AdminMonitors and LogoutButton),
// each written on its own. They had already drifted: two cleared the cached
// `userRole` and four did not, and LogoutButton never called signOut at all.
// None of them cleared the active tenant/site, which is the bug this fixes —
// see clearActiveTenant() in activeTenant.js for why localStorage alone is not
// enough.
//
// Deliberately does NOT navigate. The callers disagree about where logging out
// should land (CoordinatorPage goes to "/", everyone else to "/login"), and
// that is a routing decision, not a session one. Each caller keeps its own
// redirect; only the tearing-down is shared.

import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { clearActiveTenant } from "./activeTenant";

/**
 * End the session and forget everything tied to the person who was signed in.
 *
 * Never throws: a failed signOut must not leave the caller stuck on the page
 * with their site still selected. The local state is cleared either way, and
 * the caller navigates away regardless — matching what ReferralPage's handler
 * already did by hand.
 */
export async function logoutAndClearSite() {
  try {
    await signOut(auth);
  } catch {
    /* ignore — clear local state and let the caller redirect anyway */
  }

  // The previous user's site must not survive into the next session.
  clearActiveTenant();

  try {
    // Cached role hint, written at login. Two of the seven handlers cleared it
    // and the rest left it behind, so a signed-out browser kept the last user's
    // role in storage. It is only a hint — RequireAuth re-reads user_roles — but
    // it should not outlive the session.
    window.localStorage.removeItem("userRole");
  } catch {
    /* non-fatal */
  }
}
